import { isTestEnvironment } from "../apiCaching/POST-Requests/helperModules/helperFunctions";
import { apiConfig } from "../config/apiConfig";
import { userBrowserDetails } from "../util/checkDeviceAndBrowserType";
import { isTouchDevice } from "../util/device-detection";
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { getPageSettings } from "../util/pageSettings";
import { isWhitelistedAPI } from './logging-whitelisting';

// Track recent logs to prevent duplicates
const recentLogs = new Map<string, number>();
const LOG_COOLDOWN = 6000; // 6 seconds
const MAX_LOG_CACHE_SIZE = 100; // Prevent memory leaks

// Export function to clear logs for testing
export function clearRecentLogs(): void {
    recentLogs.clear();
}

function removeApiKey(url: string): string {
    const urlObj = new URL(url);
    // Check if 'apikey' parameter exists
    if (urlObj.searchParams.has('apikey')) {
        urlObj.searchParams.delete('apikey');
    }
    return urlObj.toString();
}
export function requestLogger(payload:any, callingURL?:string){ 
    if(!payload){
        console.log("No payload found");
        return;
    }
   
    console.log('%c[FAKE BOOKING]%c Mock Request Payload :','background:green; color:#f3f3f3; font-weight: bold; padding: 0 8px; font-size:1.2rem', '', payload);
    sessionStorage.setItem('mockRequestPayload',JSON.stringify(payload));
    const logData = {
        httpStatusCode: '418',
        message: `${JSON.stringify(payload)} - JS Version: ${BUILD_VERSION ?? 'noversion'} - Source URL - ${window.location.href}` ,
        timeStamp: new Date(),
        apiEndpoint: removeApiKey(callingURL ?? ''),
        deviceType: isTouchDevice() ? `Mobile Device - UserAgent: ${navigator.userAgent}` : `Desktop Device - UserAgent: ${navigator.userAgent}`
    };
    callErrorLogger(logData);
}

export function getRequestLogger(callingURL?:string, errMsg?:string){
    if(!isWhitelistedAPI({url: callingURL ?? ''}))
        return;
    
    // Create unique key for this error
    const logKey = `${callingURL ?? 'unknown'}-${errMsg ?? 'unknown'}`;
    const now = Date.now();
    
    // Check if this error was recently logged
    const lastLogged = recentLogs.get(logKey);
    if (lastLogged && (now - lastLogged) < LOG_COOLDOWN) {
        // Skip duplicate log within cooldown period
        if (isTestEnvironment()) {
            console.log(`[getRequestLogger] Skipped duplicate log for: ${callingURL}`);
        }
        return;
    }
    
    // Clean up old entries if cache is too large
    if (recentLogs.size >= MAX_LOG_CACHE_SIZE) {
        const entriesToDelete: string[] = [];
        recentLogs.forEach((timestamp, key) => {
            if ((now - timestamp) > LOG_COOLDOWN) {
                entriesToDelete.push(key);
            }
        });
        entriesToDelete.forEach(key => recentLogs.delete(key));
    }
    
    // Record this log
    recentLogs.set(logKey, now);
    
    const logData = {
        httpStatusCode: '400',
        message: `Source URL - ${window.location.href} - JS Version: ${BUILD_VERSION ?? 'noversion'} - GET Request failed due to error: ${errMsg}` ,
        timeStamp: new Date(),
        apiEndpoint: removeApiKey(callingURL ?? ''),
        deviceType: isTouchDevice() ? `Mobile Device - UserAgent: ${navigator.userAgent}` : `Desktop Device - UserAgent: ${navigator.userAgent}`
    };
    callErrorLogger(logData);
}

export async function successLogger(payload:any, callingURL?:string, tosend?:boolean, response?:any){
    if(!isWhitelistedAPI({url: callingURL ?? ''}))
        return;
    
    if(!payload || !tosend){
        return;
    }

    const userData = await userBrowserDetails();
    const sessionID = localStorage.getItem('sessionIDFr');
    const correlationID = sessionStorage.getItem('correlationIDFr');

    const fullLog = extractPageDetails();
    
    const customData = {
        brand : (document.getElementById('conceptCode') as HTMLInputElement)?.value ?? "N/A",
        sessionID : sessionID ?? "N/A",
        correlationID: correlationID ?? "N/A",
        requestPage: fullLog.requestPage,
        jsVersion: BUILD_VERSION ?? fullLog.codeVersions?.jsVersion ?? 'no version detected',
        requestComponentId: {
            buttonId : fullLog.requestComponentId.buttonId,
            formId: fullLog.requestComponentId.formId,
            formClasses : fullLog.requestComponentId.formClasses
        },
        response: response ?? "N/A",
        formTemplate: fullLog.pageScope,
        webLocationId: payload.webLocationId ?? "N/A"
    }
    

    const logData = {
        httpStatusCode: '418',
        message: JSON.stringify(customData),
        timeStamp: new Date(),
        apiEndpoint: removeApiKey(callingURL ?? ''),
        deviceType: JSON.stringify(userData)
    }
    callErrorLogger(logData);
}

export function extractPageDetails(){
    const pageScope = getPageSettings('pagetemplate');
    const clickedButton = document.activeElement;

    let buttonId = "N/A";
    let formId = "N/A";
    let formClasses: string[] = [];
    if (clickedButton && clickedButton.tagName === "BUTTON") {
        const parentForm = clickedButton.closest("form");
        buttonId = clickedButton.id ?? "N/A";
        formId = parentForm?.id ?? "N/A";
        formClasses = parentForm?.classList ? Array.from(parentForm.classList) : [];
    }
    const codeVersions = window.versionDetail;
     return {
       requestComponentId: {
            buttonId,
            formId,
            formClasses: formClasses
        },
        requestPage: window.location.href,
        pageScope,
        codeVersions
    }
}

async function callErrorLogger(logData: any) {
    const testEnvironment = isTestEnvironment();
    const url = `${window.location.origin}/${apiConfig.ApiLogerrorEndpoint}`;
    if (!logData.apiEndpoint) {
        if (testEnvironment) console.log("Error in determining the API endpoint of the failed call");
        return;
    }

    if (logData.apiEndpoint !== url) {
        sendLogRequest(url, { source: "js-code", errorMessages: [logData] }, testEnvironment);
    } else if (testEnvironment) {
        console.error("Error Logger API failed.");
    }
}
async function sendLogRequest(url: string, data: any, testEnvironment: boolean) {
    const request = {
        method: 'POST',
        url,
        data
    };

    try {
        if (testEnvironment) console.log(request);
        const response = await loggerRequest(request);
        if (testEnvironment) console.log('Log sent to URL Transformer:', response);
    } catch (err) {
        if (testEnvironment) console.error('Failed to send log to URL Transformer', err);
    }
}
const loggerURLTransformer = axios.create({
    baseURL: process.env.API_URL,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'apikey': process.env.URL_TRANSFORMER_ERROR_LOG_KEY,
    }
});
export async function loggerRequest<T>(config: AxiosRequestConfig): Promise<T> {
    try {
        const response: AxiosResponse<T> = await loggerURLTransformer.request(config);
        return response.data;
    } catch (error: any) {
        console.log("Failed to send the mocklog");
        return error;
    }
}
